/**
 * @fileoverview Walkthrough Registry
 * @description Central registry for domain-specific walkthrough definitions.
 * Domains register their walkthroughs here, enabling scalability —
 * new features simply call registerWalkthrough() with their definition.
 * @module features/walkthrough/walkthrough-registry
 */

import type { WalkthroughDefinition } from './types';

/** Map of walkthrough ID → definition */
const registry = new Map<string, WalkthroughDefinition>();

/**
 * Register a walkthrough definition. Called by each domain module.
 * If a walkthrough with the same ID already exists, it will be replaced
 * (useful for hot-reload in development).
 */
export function registerWalkthrough(definition: WalkthroughDefinition): void {
  registry.set(definition.id, definition);
}

/**
 * Retrieve a walkthrough definition by ID.
 * Returns undefined if not registered.
 */
export function getWalkthrough(id: string): WalkthroughDefinition | undefined {
  return registry.get(id);
}

/**
 * Get all registered walkthrough definitions.
 */
export function getAllWalkthroughs(): WalkthroughDefinition[] {
  return Array.from(registry.values());
}

/**
 * Get all walkthroughs for a specific domain.
 */
export function getWalkthroughsByDomain(domain: string): WalkthroughDefinition[] {
  return Array.from(registry.values()).filter(w => w.domain === domain);
}

/**
 * Check if a walkthrough is registered.
 */
export function hasWalkthrough(id: string): boolean {
  return registry.has(id);
}
